/**
 * 電話・FAX・携帯の3つの欄（2026-09-19 射場さん。Board の「TEL [ ]-[ ]-[ ]」の形に、自動で次の欄へ進むしくみを足した）。
 *   - 頭の数字で区切りが決まったら、自動で次の欄へ（03・06 → 2桁、090・080・070・050 → 3桁、0120 → 4桁 …）。
 *     区切りは総務省の番号表から作った表で引く（packages/contract の phone.ts）。全国の市外局番に効く
 *   - 決まらないうちは待つ（「042」は 042-6xx 八王子・0422 武蔵野・04-29xx 所沢 に分かれるので、次の数字で決まる）
 *   - 2つ目の欄も、決まった桁数を打つと3つ目へ。3つ目で止まる（欄の外へは勝手に出ない）
 *   - 「-」を打つか Tab で自分で次へ進める。自動で進んだ直後の「-」「Tab」は飲み込む（二重に進まない）
 *   - 空の欄で Backspace → 前の欄へ。←→ で欄の端から隣へ
 *   - 貼り付け・まとめ打ち「06-6429-4800」「0664294800」は3つに分けて入る
 *   - 全角で打っても半角に（日本語入力のまま打っても、確定したときに直して進む。変換中は動かない）
 * 値は1つの文字列（「06-6429-4800」）で親とやり取りする。保存の形は今までと同じ
 */
import { joinPhone, phoneGroupsForArea, phoneGroupsOf, splitPhone, toHalfPhone } from "@genbaryoku/contract";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { guardComposingEnter } from "./ui.jsx";

type Parts = [string, string, string];
const digits = (s: string) => toHalfPhone(s).replace(/[^0-9]/g, "");
const SEP = /[-()\s　]/;

/** 1つの欄に打った（貼った）文字から、3つの欄の中身と次に置くカーソルを決める。画面の外からも確かめられるよう純粋な関数にしてある */
export function nextPhoneParts(prev: Parts, i: 0 | 1 | 2, raw: string): { parts: Parts; focus: number; advanced: boolean } {
  const t = toHalfPhone(raw);
  const later = prev.slice(i + 1).every((p) => p === ""); // うしろの欄が空＝前から順に打っている
  // 区切りの入った文字（貼り付け・日本語入力の「－」）：区切りどおりに分ける
  if (SEP.test(t)) {
    const segs = t.split(/[-()\s　]+/).map(digits);
    if (i === 0 && later) {
      const s = splitPhone(t);
      const parts: Parts = [s[0], s[1], s[2]];
      return { parts, focus: parts[1] === "" ? 1 : 2, advanced: true };
    }
    if (i === 1 && prev[2] === "") {
      const parts: Parts = [prev[0], segs[0] ?? "", segs.slice(1).join("")];
      return { parts, focus: 2, advanced: true };
    }
    const parts = [...prev] as Parts;
    parts[i] = segs.join("");
    return { parts, focus: i, advanced: false };
  }
  const d = digits(t);
  if (i === 0 && later) {
    const g = phoneGroupsOf(d);
    if (g && d.length >= g[0]) {
      const rest = d.slice(g[0]);
      const parts: Parts = [d.slice(0, g[0]), rest.slice(0, g[1]), rest.slice(g[1])];
      const focus = parts[1].length >= g[1] ? 2 : 1;
      return { parts, focus, advanced: true };
    }
    return { parts: [d, "", ""], focus: 0, advanced: false };
  }
  if (i === 1 && prev[2] === "") {
    // 「080」のあとに「0」＝ フリーダイヤルの 0800（携帯の 080-0xxx は無い）
    if (prev[0] === "080" && d.startsWith("0")) return nextPhoneParts(["", "", ""], 0, `0800${d.slice(1)}`);
    const g = phoneGroupsForArea(prev[0]);
    if (g && d.length >= g[1]) return { parts: [prev[0], d.slice(0, g[1]), d.slice(g[1])], focus: 2, advanced: true };
    return { parts: [prev[0], d, ""], focus: 1, advanced: false };
  }
  const parts = [...prev] as Parts;
  parts[i] = d;
  return { parts, focus: i, advanced: false };
}

export function PhoneFields({
  id,
  label,
  value,
  onChange,
  invalid = false,
  autoComplete,
  autoFocus = false,
}: {
  /** 1つ目の欄の id（ラベルと結ぶ。保存の間違いでここへカーソルを送る） */
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  const [parts, setParts] = useState<Parts>(() => splitPhone(value));
  const partsRef = useRef(parts);
  partsRef.current = parts;
  const refs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];
  const pending = useRef<{ i: number; pos: "start" | "end" } | null>(null);
  const justAdvanced = useRef(-1); // 自動で進んだ先の欄（その直後の「-」「Tab」を飲み込む）
  const composing = useRef(false);
  const labelId = `${id}-label`;
  const descId = `${id}-desc`;

  // 親から値が変わったら（読み込み・保存のあと・フォームを空に）欄を分け直す。自分で打っている間は値が同じなので動かない
  useEffect(() => {
    if (joinPhone(partsRef.current) !== value) setParts(splitPhone(value));
  }, [value]);

  useLayoutEffect(() => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    const el = refs[p.i]?.current;
    if (!el) return;
    el.focus();
    const n = p.pos === "end" ? el.value.length : 0;
    el.setSelectionRange(n, n);
  });

  const commit = (next: Parts, focus: number, advanced: boolean, from: number) => {
    setParts(next);
    onChange(joinPhone(next));
    if (focus !== from) pending.current = { i: focus, pos: "end" };
    justAdvanced.current = advanced && focus !== from ? focus : -1;
  };
  const input = (i: 0 | 1 | 2, raw: string) => {
    const r = nextPhoneParts(partsRef.current, i, raw);
    commit(r.parts, r.focus, r.advanced, i);
  };
  const move = (i: number, pos: "start" | "end") => {
    pending.current = { i, pos };
    setParts((p) => [...p] as Parts); // 描き直してカーソルを置く
  };

  const onKeyDown = (i: 0 | 1 | 2) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    guardComposingEnter(e);
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const el = e.currentTarget;
    const empty = parts[i] === "";
    const swallow = justAdvanced.current === i && empty;
    if (e.key === "-" || e.key === "－" || e.key === "ー") {
      e.preventDefault();
      justAdvanced.current = -1;
      if (!swallow && i < 2) move(i + 1, "end");
      return;
    }
    if (e.key === "Tab" && !e.shiftKey && swallow) {
      e.preventDefault();
      justAdvanced.current = -1;
      return;
    }
    if (e.key !== "Tab" && e.key !== "Shift") justAdvanced.current = -1;
    const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
    const atEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
    if (e.key === "Backspace" && empty && i > 0) {
      e.preventDefault();
      move(i - 1, "end");
    } else if (e.key === "ArrowLeft" && atStart && i > 0) {
      e.preventDefault();
      move(i - 1, "end");
    } else if (e.key === "ArrowRight" && atEnd && i < 2) {
      e.preventDefault();
      move(i + 1, "start");
    }
  };

  const box = (i: 0 | 1 | 2) => (
    <input
      ref={refs[i]}
      id={i === 0 ? id : `${id}-${i + 1}`}
      className="inp"
      type="text"
      inputMode="numeric"
      autoComplete={i === 0 ? (autoComplete ?? "off") : "off"}
      autoFocus={i === 0 && autoFocus}
      aria-label={i === 0 ? undefined : `${i + 1}つ目の欄`}
      aria-describedby={i === 0 ? descId : labelId}
      aria-invalid={invalid || undefined}
      value={parts[i]}
      onKeyDown={onKeyDown(i)}
      onPaste={(e) => {
        // 番号を丸ごと貼ったら（10桁以上か区切り入り）3つに分けて入れ直す
        const text = e.clipboardData.getData("text");
        if (digits(text).length >= 10 || (SEP.test(text.trim()) && digits(text).length >= 6)) {
          e.preventDefault();
          const s = splitPhone(text);
          commit([s[0], s[1], s[2]], 2, false, i);
        }
      }}
      onCompositionStart={() => {
        composing.current = true;
      }}
      onCompositionEnd={(e) => {
        composing.current = false;
        input(i, e.currentTarget.value);
      }}
      onChange={(e) => {
        if (composing.current) {
          const v = e.target.value;
          setParts((p) => {
            const n = [...p] as Parts;
            n[i] = v;
            return n;
          });
        } else input(i, e.target.value);
      }}
      onBlur={() => {
        if (justAdvanced.current === i) justAdvanced.current = -1;
      }}
    />
  );

  return (
    <div className="field">
      <label id={labelId} htmlFor={id}>{label}</label>
      {/* 自動で次の欄へ進むことを、読み上げで先に伝える（WCAG 3.2.2 の G13） */}
      <span id={descId} className="sr">3つの欄に分けて入れます。区切りのところで自動で次の欄へ進みます</span>
      <div className="split tel">
        {box(0)}
        <span className="sep" aria-hidden="true">-</span>
        {box(1)}
        <span className="sep" aria-hidden="true">-</span>
        {box(2)}
      </div>
    </div>
  );
}
